import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface JobTag {
  job_tag_id: string;
  tag_name: string;
  description: string;
  is_active: boolean;
  created_at: string;
}

interface User {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  location?: string;
  assignedAt?: string;
}

const JobTagManagement: React.FC = () => {
  const [jobTags, setJobTags] = useState<JobTag[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJobTag, setSelectedJobTag] = useState<JobTag | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [newUser, setNewUser] = useState('');
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [formData, setFormData] = useState({
    tag_name: '',
    description: '',
  });

  useEffect(() => {
    fetchJobTags();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedJobTag) {
      fetchTagUsers(selectedJobTag.job_tag_id);
    }
  }, [selectedJobTag]);

  const fetchJobTags = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/job-tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setJobTags(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching job tags:', error);
      setError('Failed to fetch tags');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError((prev) => prev ?? 'Failed to fetch users');
    }
  };

  const fetchTagUsers = async (tagId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/job-tags/${tagId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSelectedUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching tag users:', error);
    }
  };

  const handleOpenDialog = (tag?: JobTag | null) => {
    if (tag) {
      setSelectedJobTag(tag);
      setFormData({
        tag_name: tag.tag_name ?? '',
        description: tag.description ?? '',
      });
    } else {
      setSelectedJobTag(null);
      setFormData({
        tag_name: '',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJobTag(null);
  };

  const handleCloseAssignDialog = () => {
    setOpenAssignDialog(false);
    setNewUser('');
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const url = selectedJobTag
        ? `${API_BASE_URL}/job-tags/${selectedJobTag.job_tag_id}`
        : `${API_BASE_URL}/job-tags`;
      const method = selectedJobTag ? 'PUT' : 'POST';

      const payload = selectedJobTag
        ? {
            tag_name: formData.tag_name,
            description: formData.description,
            is_active: selectedJobTag.is_active,
          }
        : formData;

      await axios({
        method,
        url,
        data: payload,
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchJobTags();
      handleCloseDialog();
    } catch (error: unknown) {
      console.error('Error saving tag:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      setError(axiosErr.response?.data?.message || 'Failed to save tag');
    }
  };

  const handleDelete = async (tagId: string) => {
    if (!window.confirm('Are you sure you want to delete this tag?')) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      await axios.delete(`${API_BASE_URL}/job-tags/${tagId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchJobTags();
    } catch (error) {
      console.error('Error deleting job tag:', error);
      setError('Failed to delete tag');
    }
  };

  const handleViewUsers = async (jobTag: JobTag) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/job-tags/${jobTag.job_tag_id}/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSelectedJobTag(jobTag);
      setSelectedUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching job tag users:', error);
      setError('Failed to fetch users for this tag');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignUser = async () => {
    if (!selectedJobTag || !newUser) return;

    try {
      setLoading(true);
      await axios.post(
        `${API_BASE_URL}/job-tags/${selectedJobTag.job_tag_id}/users`,
        { userId: newUser },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Refresh the user list
      const response = await axios.get(`${API_BASE_URL}/job-tags/${selectedJobTag.job_tag_id}/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSelectedUsers(response.data.users);
      setNewUser('');
      setOpenAssignDialog(false);
    } catch (error) {
      console.error('Error assigning user:', error);
      setError('Failed to assign user to tag');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!selectedJobTag) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      await axios.delete(
        `${API_BASE_URL}/job-tags/${selectedJobTag.job_tag_id}/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh the user list
      const response = await axios.get(`${API_BASE_URL}/job-tags/${selectedJobTag.job_tag_id}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUsers(response.data.users);
    } catch (error) {
      console.error('Error removing user:', error);
      setError('Failed to remove user from tag');
    }
  };

  const availableUsers = users.filter(
    user => !selectedUsers.some(selected => selected.userId === user.userId)
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Tags
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create tag
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {jobTags.map((tag) => (
          <Grid item xs={12} sm={6} md={4} key={tag.job_tag_id}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom>
                  {tag.tag_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {tag.description || 'No description'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedUsers
                    .filter(user => user.userId === tag.job_tag_id)
                    .map(user => (
                      <Chip
                        key={user.userId}
                        label={`${user.firstName} ${user.lastName}`}
                        onDelete={() => handleRemoveUser(user.userId)}
                        size="small"
                      />
                    ))}
                </Box>
              </CardContent>
              <CardActions>
                <Tooltip title="Edit tag">
                  <IconButton size="small" onClick={() => handleOpenDialog(tag)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="View users">
                  <IconButton size="small" onClick={() => handleViewUsers(tag)}>
                    <PersonIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete tag">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(tag.job_tag_id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {selectedJobTag ? 'Edit tag' : 'Create tag'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tag Name"
            fullWidth
            value={formData.tag_name}
            onChange={(e) => setFormData({ ...formData, tag_name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} color="primary">
            {selectedJobTag ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Users Dialog — hide while create/edit dialog is open (same selectedJobTag used for edit) */}
      <Dialog
        open={selectedJobTag !== null && !openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedJobTag?.tag_name} - Assigned Users
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenAssignDialog(true)}
                  startIcon={<PersonAddIcon />}
                >
                  Assign User
                </Button>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Assigned At</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedUsers.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.role}</TableCell>
                        <TableCell>{user.location || 'N/A'}</TableCell>
                        <TableCell>
                          {user.assignedAt
                            ? new Date(user.assignedAt).toLocaleDateString()
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveUser(user.userId)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog
        open={openAssignDialog}
        onClose={handleCloseAssignDialog}
      >
        <DialogTitle>Assign User to {selectedJobTag?.tag_name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={newUser}
                onChange={(e) => setNewUser(e.target.value)}
                label="Select User"
              >
                  {availableUsers.length > 0 ? (
                    availableUsers.map((user) => (
                      <MenuItem key={user.userId} value={user.userId}>
                        {`${user.firstName} ${user.lastName} (${user.email})`}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      {users.length === 0 ? 'No users available' : 'All users already assigned'}
                    </MenuItem>
                  )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog}>Cancel</Button>
          <Button
            onClick={handleAssignUser}
            variant="contained"
            color="primary"
            disabled={!newUser}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobTagManagement; 